/**
 * Swiper initialization for custom carousel
 * Handles Shopify Theme Editor events and safe configuration parsing
 */

function initCustomCarousels(scope = document) {
  // Select all elements with [e-swiper-slider] inside scope (custom carousel)
  const swiperElements = scope.querySelectorAll('[e-swiper-slider]');

  swiperElements.forEach((el) => {
    /** @type {ElementWithSwiper} */
    const swiperEl = el;

    // Prevent duplicate initialization
    if (swiperEl.swiper) {
      return;
    }

    // Check if swiper-wrapper already exists (section renders it directly)
    const swiperWrapper = swiperEl.querySelector('.swiper-wrapper');
    if (!swiperWrapper) {
      console.warn('Swiper wrapper not found');
      return;
    }

    // Check if there are slides
    const slides = swiperWrapper.querySelectorAll('.swiper-slide');
    if (slides.length === 0) {
      console.warn('No swiper slides found');
      return;
    }

    // Parse JSON safely from data-config attribute
    /** @type {any} */
    let config = {};
    try {
      const configString = swiperEl.dataset?.config || 'slidesPerView:1, spaceBetween:10, loop:true';

      // Handle both JSON format and simplified format
      if (configString.trim().startsWith('{')) {
        config = JSON.parse(configString);
      } else {
        // Parse simplified format: "slidesPerView:1, spaceBetween:10, loop:true"
        config = {};
        configString.split(',').forEach(pair => {
          const [key, value] = pair.trim().split(':');
          if (key && value) {
            const trimmedKey = key.trim();
            const trimmedValue = value.trim();

            // Convert string values to appropriate types
            if (trimmedValue === 'true') config[trimmedKey] = true;
            else if (trimmedValue === 'false') config[trimmedKey] = false;
            else if (!isNaN(Number(trimmedValue))) config[trimmedKey] = Number(trimmedValue);
            else config[trimmedKey] = trimmedValue;
          }
        });
      }
    } catch (error) {
      console.warn('Invalid Swiper configuration, using defaults');
      config = { slidesPerView: 1, spaceBetween: 10, loop: true };
    }

    // Build Swiper configuration
    /** @type {SwiperConfig} */
    const swiperConfig = {
      slidesPerView: config.slidesPerView || 1,
      spaceBetween: config.gap || 0,
      speed: config.speed || 300,
      loop: config.loop || false,
      direction: config.direction || 'horizontal',
      grid: config.rows ? { rows: config.rows } : undefined,
      autoplay: config.autoplay && config.autoplay.enabled ? {
        delay: config.autoplay.delay || 3000,
        disableOnInteraction: false
      } : false,
      breakpoints: config.breakpoints || {}
    };

    // Navigation logic - auto-detect if elements exist in DOM
    const prevEl = swiperEl.querySelector('.swiper-button-prev');
    const nextEl = swiperEl.querySelector('.swiper-button-next');
    
    if (prevEl && nextEl) {
      swiperConfig.navigation = {
        prevEl: prevEl,
        nextEl: nextEl
      };
    }

    // Pagination logic - auto-detect if element exists in DOM
    const paginationEl = swiperEl.querySelector('.swiper-pagination');
    
    if (paginationEl) {
      swiperConfig.pagination = {
        el: paginationEl,
        type: 'bullets',
        clickable: true
      };
    }

    // Initialize Swiper
    /** @type {WindowWithSwiper} */
    const windowWithSwiper = window;
    if (windowWithSwiper.Swiper) {
      swiperEl.swiper = new windowWithSwiper.Swiper(swiperEl, swiperConfig);
    } else {
      console.warn('Swiper library not loaded');
    }
  });
}

/**
 * Resolve selector string to DOM element
 * First tries scope.querySelector, then document.querySelector
 * @param {string} selector
 * @param {Document|Element} scope
 * @returns {Element|null}
 */
function resolveSelector(selector, scope) {
  if (typeof selector !== 'string') return null;

  try {
    // Try scope first
    let element = scope.querySelector(selector);
    if (element) return element;

    // Fallback to document
    element = document.querySelector(selector);
    return element;
  } catch (error) {
    return null;
  }
}

// Theme Editor support
document.addEventListener("shopify:section:load", /** @param {Event} e */ e => {
  if (e.target) {
    initCustomCarousels(/** @type {Document} */ (e.target));
  }
});

document.addEventListener("shopify:section:unload", /** @param {Event} e */ e => {
  if (e.target && 'querySelectorAll' in e.target) {
    const target = /** @type {ElementWithSwiper} */ (e.target);
    if ('querySelectorAll' in target) {
      target.querySelectorAll('[e-swiper-slider]').forEach((el) => {
        /** @type {ElementWithSwiper} */
        const swiperEl = el;
        if (swiperEl.swiper) {
          swiperEl.swiper.destroy(true, true);
          swiperEl.swiper = null;
        }
      });
    }
  }
});

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  initCustomCarousels();
});

// Export for potential external use
window.initCustomCarousels = initCustomCarousels;
